import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from './axiosSetup';
import 'bootstrap/dist/css/bootstrap.min.css';
import './css/Login.css' // Optional: für extra Styles
import cfg from '../config';
import InstallPrompt from './InstallPrompt';
import LicenseBlocked from './LicenseBlocked';
import { useApp } from '../AppContext';

const Login = () => {
    const location = useLocation();
    const { lizenz } = useApp();
    const [message, setMessage] = useState(location.state?.message || '');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [street, setStreet] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [city, setCity] = useState('');
    const [IBAN, setIBAN] = useState('');
    const [ibanError, setIbanError] = useState('');
    const [passwordMatchError, setPasswordMatchError] = useState('');
    const [registerSuccess, setRegisterSuccess] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);
    const [agbAccepted, setAgbAccepted] = useState(false);
    const [showAGBModal, setShowAGBModal] = useState(false);
    const [animateModal, setAnimateModal] = useState(false);

    useEffect(() => {
        if (location.state?.message) setMessage(location.state.message);
    }, [location.state?.message]);

    // Animation beim Öffnen
    const openAGBModal = () => {
        setShowAGBModal(true);
        setTimeout(() => setAnimateModal(true), 10); // kleine Verzögerung für Transition
    };

    // Animation beim Schließen
    const closeAGBModal = () => {
        setAnimateModal(false);
        setTimeout(() => setShowAGBModal(false), 300); // Zeit entspricht Transition
    };

    const handleLogin = async () => {
        try {
            const response = await axios.post('/api/login', { email, password });
            const data = response.data;
            if (data.token) {
                if (data.isActivated) {
                    localStorage.setItem('authToken', data.token);
                    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
                    localStorage.setItem('firstName', data.firstName);
                    localStorage.setItem('lastName', data.lastName);
                    localStorage.setItem('isZunftratAdmin', data.isZunftratAdmin);
                    localStorage.setItem('userID', data.userId);
                    localStorage.setItem('onboardingCompleted', data.onboardingCompleted ? '1' : '0');
                    if (data.mustChangePassword) {
                        window.location.href = '/change-password-forced';
                    } else if (!data.onboardingCompleted) {
                        window.location.href = '/onboarding';
                    } else {
                        window.location.href = '/dashboard';
                    }
                } else {
                    setMessage(`Dein Zugang ist noch nicht aktiviert. Bitte melde dich bei ${cfg.adminRoleName}.`);
                }
            } else {
                setMessage("Login fehlgeschlagen: " + data.message);
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || "Unbekannter Fehler";
            const errCode = err.response?.data?.code;
            if (errCode === 'EMAIL_NOT_VERIFIED') {
                setMessage("Bitte bestätige zuerst deine E-Mail-Adresse. Schau in dein Postfach.");
            } else {
                setMessage("Fehler beim Login: " + errMsg);
            }
        }
    };

    const handleRegister = async () => {
        if (!agbAccepted) {
            setMessage("Bitte akzeptiere die AGB, um dich zu registrieren.");
            return;
        }
        if (password !== confirmPassword) {
            setPasswordMatchError("Die Passwörter stimmen nicht überein!");
            return;
        }
        try {
            const response = await axios.post('/api/register', {
                email, password, firstName, lastName, birthDate, street, houseNumber, city, IBAN
            });
            const data = response.data;
            if (data.success) {
                setMessage("Registrierung erfolgreich! Bitte bestätige deine E-Mail-Adresse (schau in dein Postfach).");
                setIsRegistering(false);
                setRegisterSuccess(true);
                setTimeout(() => setMessage(''), 8000);
            } else {
                setMessage("Fehler bei der Registrierung: " + data.message);
            }
        } catch (err) {
            setMessage("Fehler bei der Registrierung: " + err.message);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case 'email': setEmail(value); break;
            case 'password': setPassword(value); break;
            case 'confirmPassword': setConfirmPassword(value); break;
            case 'firstName': setFirstName(value); break;
            case 'lastName': setLastName(value); break;
            case 'birthDate': setBirthDate(value); break;
            case 'street': setStreet(value); break;
            case 'houseNumber': setHouseNumber(value); break;
            case 'city': setCity(value); break;
            case 'IBAN': setIBAN(value); break;
            default: break;
        }
    };

    const validateIBAN = (iban) => /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban);
    const handleIBANChange = (e) => {
        const iban = e.target.value;
        setIBAN(iban);
        setIbanError(validateIBAN(iban) ? '' : 'Ungültige IBAN');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isRegistering && ibanError) {
            alert("Bitte eine gültige IBAN eingeben.");
            return;
        }
        if (isRegistering) handleRegister();
        else handleLogin();
    };

    const toggleForm = () => setIsRegistering(!isRegistering);

    // Kein Login-Formular zeigen, wenn der Server die Anmeldung ohnehin ablehnt —
    // sonst tippt jemand seine Zugangsdaten ein und bekommt nur eine Fehlermeldung.
    if (lizenz?.systemLocked || lizenz?.blocked) {
        return (
            <LicenseBlocked
                status={lizenz.status}
                expDate={lizenz.expDate}
                customer={lizenz.customer}
                systemLocked={lizenz.systemLocked}
                lockedMessage={lizenz.systemLockedMessage}
            />
        );
    }

    return (
        <div className="d-flex flex-column min-vh-100"
            style={{
                backgroundImage: "url('/assets/img/narrenzunft-bg.jpg')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}>
                {/* Karnevals-Confetti */}
                {/* InstallPrompt Banner einfügen */}
                <InstallPrompt />
   
            <div className="container d-flex justify-content-center align-items-center flex-grow-1">
                <form
                    onSubmit={handleSubmit}
                    className="bg-dark text-light p-4 rounded shadow-lg w-100"
                    style={{ maxWidth: '500px', opacity: 0.95, marginBottom: '30px' }}
                >
                    <h2 className="text-center mb-4">{isRegistering ? 'Registrieren' : `${cfg.orgName} Login`}</h2>
                    

                    <div className="mb-3">
                        <label className="form-label">Email:</label>
                        <input type="email" name="email" value={email} onChange={handleChange} className="form-control" required />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Passwort:</label>
                        <input type="password" name="password" value={password} onChange={handleChange} className="form-control" required />
                    </div>

                    {isRegistering && (
                        <>
                            <div className="mb-3">
                                <label className="form-label">Passwort bestätigen:</label>
                                <input type="password" name="confirmPassword" value={confirmPassword} onChange={handleChange} className="form-control" required />
                                {passwordMatchError && <div className="text-danger">{passwordMatchError}</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Vorname:</label>
                                <input type="text" name="firstName" value={firstName} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Nachname:</label>
                                <input type="text" name="lastName" value={lastName} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Geburtsdatum:</label>
                                <input type="date" name="birthDate" value={birthDate} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Straße:</label>
                                <input type="text" name="street" value={street} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Hausnummer:</label>
                                <input type="text" name="houseNumber" value={houseNumber} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Ort:</label>
                                <input type="text" name="city" value={city} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">IBAN:</label>
                                <input type="text" name="IBAN" value={IBAN} onChange={handleIBANChange} className={`form-control ${ibanError ? 'is-invalid' : ''}`} required />
                                {ibanError && <div className="invalid-feedback">{ibanError}</div>}
                            </div>

                            <div className="mb-3 form-check">
                                <input type="checkbox" className="form-check-input" id="agbCheck" checked={agbAccepted} onChange={() => setAgbAccepted(!agbAccepted)} />
                                <label className="form-check-label">
                                    Ich akzeptiere die <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={openAGBModal}>Datenschutzbestimmungen</span>
                                </label>
                            </div>
                        </>
                    )}

                    <button type="submit" className="btn btn-warning w-100 mb-3">
                        {isRegistering ? 'Registrieren' : 'Anmelden'}
                    </button>

                    <button type="button" className="btn btn-outline-light w-100" onClick={toggleForm}>
                        {isRegistering ? 'Zum Login wechseln' : 'Registrieren'}
                    </button>

                    {!isRegistering && (
                        <div className="text-center mt-3">
                            <Link to="/passwort-vergessen" className="text-secondary small">
                                Passwort vergessen?
                            </Link>
                        </div>
                    )}
                    <br/>

                    {message && <div className="alert alert-warning">{message}</div>}

                    {registerSuccess && (
                        <div className={`alert mt-3 ${registerSuccess ? 'alert-success' : 'alert-danger'}`}>
                            {registerSuccess ? "Registrierung erfolgreich! Bitte einloggen." : message}
                        </div>
                    )}
                </form>
            </div>

            {/* AGB Modal mit Animation */}
            {showAGBModal && (
                <div className={`modal-backdrop ${animateModal ? 'fade-in' : 'fade-out'}`}>
                    <div className={`modal show d-block ${animateModal ? 'fade-in' : 'fade-out'}`} tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Nutzungsbedingungen & Datenschutz</h5>
                                    <button type="button" className="btn-close" aria-label="Close" onClick={closeAGBModal}></button>
                                </div>
                               <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    <h6>1. Geltungsbereich</h6>
                                    <p>
                                        Diese Nutzungsbedingungen regeln die Nutzung der Vereins-App von
                                        <strong> {cfg.orgFullName}</strong> (nachfolgend „Verein”).
                                        Die App richtet sich ausschließlich an Mitglieder des Vereins.
                                    </p>

                                    <h6>2. Zweck der App</h6>
                                    <p>
                                        Die App dient der vereinsinternen Organisation, Kommunikation sowie der
                                        Verwaltung von Mitgliedsdaten. Eine Nutzung zu kommerziellen oder
                                        vereinsfremden Zwecken ist untersagt.
                                    </p>

                                    <h6>3. Registrierung und Zugang</h6>
                                    <p>
                                        Die Nutzung der App setzt eine Registrierung voraus. Die bei der
                                        Registrierung gemachten Angaben müssen wahrheitsgemäß und vollständig sein.
                                        Zugangsdaten sind vertraulich zu behandeln und dürfen nicht an Dritte
                                        weitergegeben werden.
                                    </p>

                                    <h6>4. Pflichten der Nutzer</h6>
                                    <ul>
                                        <li>Die App darf nicht missbräuchlich genutzt werden.</li>
                                        <li>Rechtswidrige, beleidigende oder vereinschädigende Inhalte sind untersagt.</li>
                                        <li>Technische Manipulationen oder Umgehung von Sicherheitsmechanismen sind verboten.</li>
                                    </ul>

                                    <h6>5. Haftung</h6>
                                    <p>
                                        Der Verein haftet nicht für Schäden, die durch unsachgemäße Nutzung der App
                                        oder durch technische Störungen entstehen, soweit gesetzlich zulässig.
                                    </p>

                                    <h6>6. Beendigung der Nutzung</h6>
                                    <p>
                                        Mit Beendigung der Mitgliedschaft im Verein endet auch die Berechtigung zur
                                        Nutzung der App. Der Zugang wird in diesem Fall deaktiviert.
                                    </p>

                                    <hr />

                                    <h5>Datenschutz & Datenverarbeitung</h5>

                                    <h6>7. Verantwortlicher</h6>
                                    <p>
                                        Verantwortlich im Sinne der DSGVO ist:<br />
                                        <strong>{cfg.orgFullName}</strong><br />
                                        {cfg.orgStreet}<br />{cfg.orgPostal} {cfg.orgCity}<br />
                                        {cfg.orgEmail && <>E-Mail: {cfg.orgEmail}</>}
                                    </p>

                                    <h6>8. Verarbeitete personenbezogene Daten</h6>
                                    <ul>
                                        <li>Name, Anschrift, Geburtsdatum</li>
                                        <li>E-Mail-Adresse</li>
                                        <li>Bankverbindung (z. B. IBAN, Beitragsverwaltung)</li>
                                        <li>Login- und Nutzungsdaten (z. B. Zeitpunkte, Geräteinformationen)</li>
                                    </ul>

                                    <h6>9. Zweck der Verarbeitung</h6>
                                    <p>
                                        Die Verarbeitung erfolgt zur Verwaltung der Mitgliedschaft, zur internen
                                        Kommunikation, zur Beitragsabrechnung sowie zum Betrieb der Vereins-App.
                                    </p>

                                    <h6>10. Rechtsgrundlagen</h6>
                                    <p>
                                        Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
                                        (Mitgliedschaftsverhältnis) sowie Art. 6 Abs. 1 lit. c DSGVO
                                        (gesetzliche Verpflichtungen).
                                    </p>

                                    <h6>11. Hosting und Auftragsverarbeitung</h6>
                                    <p>
                                        Die App wird bei einem externen Hosting-Dienstleister betrieben.
                                        Mit diesem besteht ein Vertrag zur Auftragsverarbeitung gemäß Art. 28 DSGVO.
                                        Eine Datenverarbeitung erfolgt ausschließlich innerhalb der EU.
                                    </p>

                                    <h6>12. Speicherdauer</h6>
                                    <p>
                                        Personenbezogene Daten werden nur so lange gespeichert, wie dies für die
                                        genannten Zwecke erforderlich ist oder gesetzliche Aufbewahrungspflichten
                                        bestehen.
                                    </p>

                                    <h6>13. Rechte der betroffenen Personen</h6>
                                    <ul>
                                        <li>Recht auf Auskunft</li>
                                        <li>Recht auf Berichtigung</li>
                                        <li>Recht auf Löschung</li>
                                        <li>Recht auf Einschränkung der Verarbeitung</li>
                                        <li>Recht auf Datenübertragbarkeit</li>
                                        <li>Widerspruchsrecht</li>
                                    </ul>

                                    <p className="text-muted mt-3">
                                        Diese Datenschutzhinweise ersetzen keine vollständige Datenschutzerklärung.
                                        Die aktuelle Version ist jederzeit über die Vereinswebsite abrufbar.
                                    </p>

                                    </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeAGBModal}>Schließen</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
<InstallPrompt />

export default Login;
